import { addInvisibleEntities, removeInvisibleEntities } from "@/Context/ViewerContext/Reducer";
import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import { setLoaderPlugin, setViewer } from "@/Context/XktContext/Reducer";
import { XktContext } from "@/Context/XktContext/XktContextProvider";
import useEntityMutations from "@/Hooks/EntityMutations/EntityMutations";
import useGlobalDstudioViewer from "@/Hooks/GlobalDstudioViewer/GlobalDstudioViewer";
import ViewerConfig from "@/Models/ViewerConfig";
import { getConfig } from "@/_Configuration/configuration";
import {
    AmbientLight,
    DirLight,
    Entity,
    FastNavPlugin,
    LocaleService,
    VBOSceneModel,
    Viewer,
    XKTLoaderPlugin,
} from "@xeokit/xeokit-sdk";
import { LoaderCircle } from "lucide-react";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface CacheItem {
    src: string;
    model: VBOSceneModel;
}

interface IfcViewerProps {
    configuration: ViewerConfig;
}

// Distance threshold to break reveal mode and recenter the camera on the new model instead of expanding the current view to include it.
const SMART_RECENTER_DISTANCE = 1000;

const IfcViewer = ({ configuration }: IfcViewerProps) => {
    const { state, dispatch } = useContext(XktContext);
    const { state: viewerState, dispatch: dispatchViewer } = useContext(ViewerContext);

    const cacheRef = useRef<Map<string, CacheItem>>(new Map());
    const loadOrderRef = useRef<CacheItem[]>([]);
    const localeService = useRef<LocaleService | null>(null);
    const lastEntityRef = useRef<Entity | null>(null);
    const flyToScheduledRef = useRef<number | null>(null);
    const accumulatedAabbRef = useRef<number[] | null>(null);
    const lastModelCenterRef = useRef<number[] | null>(null);

    const { t, i18n } = useTranslation();

    useEntityMutations();
    useGlobalDstudioViewer();

    const getCubeMessages = useCallback(
        (lng: string) => ({
            front: t("navCube.front", { lng }),
            back: t("navCube.back", { lng }),
            top: t("navCube.top", { lng }),
            bottom: t("navCube.bottom", { lng }),
            left: t("navCube.left", { lng }),
            right: t("navCube.right", { lng }),
        }),
        [t]
    );

    const createLocaleService = useCallback(() => {
        const messages = {
            en: { NavCube: getCubeMessages("en") },
            fr: { NavCube: getCubeMessages("fr") },
            nl: { NavCube: getCubeMessages("nl") },
        };

        return new LocaleService({
            messages,
            locale: i18n.language,
        });
    }, [getCubeMessages, i18n.language]);

    const getAabbCenter = (aabb: number[]) => [
        (aabb[0] + aabb[3]) / 2,
        (aabb[1] + aabb[4]) / 2,
        (aabb[2] + aabb[5]) / 2,
    ];

    const mergeAabbs = (a: number[], b: number[]) => [
        Math.min(a[0], b[0]),
        Math.min(a[1], b[1]),
        Math.min(a[2], b[2]),
        Math.max(a[3], b[3]),
        Math.max(a[4], b[4]),
        Math.max(a[5], b[5]),
    ];

    const distance = (c1: number[], c2: number[]) =>
        Math.sqrt((c1[0] - c2[0]) ** 2 + (c1[1] - c2[1]) ** 2 + (c1[2] - c2[2]) ** 2);

    const flyToModel = (cacheItem: CacheItem) => {
        if (!state.viewer) return;

        const viewer = state.viewer;
        const activeObjectIds = viewer.scene.objectIds.filter((id) => id.includes(cacheItem.model.id));
        const aabb = viewer.scene.getAABB(activeObjectIds);
        viewer.cameraFlight.flyTo({ aabb });
    };

    const scheduleFlyToAabb = (aabb: number[]) => {
        if (!state.viewer) return;

        if (flyToScheduledRef.current != null) {
            cancelAnimationFrame(flyToScheduledRef.current);
        }

        flyToScheduledRef.current = requestAnimationFrame(() => {
            state.viewer!.cameraFlight.flyTo({ aabb });
        });
    };

    const revealFlyToModel = (cacheItem: CacheItem) => {
        if (!state.viewer) return;

        const viewer = state.viewer;
        const ids = viewer.scene.objectIds.filter((id) => id.includes(cacheItem.model.id));
        const aabb = viewer.scene.getAABB(ids);
        const center = getAabbCenter(aabb);

        const accumulated = accumulatedAabbRef.current;
        const lastCenter = lastModelCenterRef.current;

        if (!accumulated) {
            accumulatedAabbRef.current = aabb;
            lastModelCenterRef.current = center;
            scheduleFlyToModel(cacheItem);
            return;
        }

        const dist = distance(center, lastCenter!);
        const expanded = mergeAabbs(accumulated, aabb);
        accumulatedAabbRef.current = expanded;

        if (dist > SMART_RECENTER_DISTANCE) {
            accumulatedAabbRef.current = aabb;
            scheduleFlyToAabb(aabb);
        } else {
            scheduleFlyToAabb(expanded);
        }

        lastModelCenterRef.current = center;
    };

    const scheduleFlyToModel = (cacheItem: CacheItem) => {
        if (!state.viewer) return;

        if (flyToScheduledRef.current !== null) {
            cancelAnimationFrame(flyToScheduledRef.current);
        }

        flyToScheduledRef.current = window.requestAnimationFrame(() => {
            flyToModel(cacheItem);
        });
    };

    const handleDefaultVisibility = () => {
        if (!state.viewer) return;

        const defaultInvisibleItems: string[] = [];

        if (!viewerState.gridEnabled && state.viewer.metaScene.metaObjectsByType["IfcGrid"]) {
            defaultInvisibleItems.push(...Object.keys(state.viewer.metaScene.metaObjectsByType["IfcGrid"]));
        }

        if (!viewerState.spaceEnabled && state.viewer.metaScene.metaObjectsByType["IfcSpace"]) {
            defaultInvisibleItems.push(...Object.keys(state.viewer.metaScene.metaObjectsByType["IfcSpace"]));
        }

        if (defaultInvisibleItems.length > 0) {
            dispatchViewer(addInvisibleEntities(defaultInvisibleItems));
        }
    };

    const setupInputEvents = (viewer: Viewer) => {
        const { scene, cameraControl } = viewer;

        const pointer = document.getElementById("camera-pivot-marker");
        if (pointer) {
            cameraControl.followPointer = true;
            cameraControl.pivotElement = pointer;
        }

        let lastTime = 0;

        // @ts-expect-error : mousemove is valid but missing in the xeokit type definitions.
        scene.input.on("mousemove", (coords) => {
            const now = performance.now();
            if (now - lastTime < 16) {
                return;
            }

            lastTime = now;

            try {
                const hit = scene.pick({ canvasPos: coords });
                if (!hit) {
                    if (lastEntityRef.current) {
                        lastEntityRef.current.highlighted = false;
                        lastEntityRef.current = null;
                    }
                    return;
                }

                if (!hit.entity || !hit.entity.isObject || !hit.entity.pickable) {
                    if (lastEntityRef.current) {
                        lastEntityRef.current.highlighted = false;
                        lastEntityRef.current = null;
                    }
                    return;
                }

                if (!lastEntityRef.current || hit.entity.id !== lastEntityRef.current.id) {
                    if (lastEntityRef.current) {
                        lastEntityRef.current.highlighted = false;
                    }

                    lastEntityRef.current = hit.entity;
                    hit.entity.highlighted = true;
                }
            } catch {
                // ignore errors
            }
        });
    };

    const setupLights = (viewer: Viewer) => {
        viewer.scene.clearLights();

        new AmbientLight(viewer.scene, {
            color: [0.7, 0.7, 0.7],
            intensity: 0.4,
        });

        new DirLight(viewer.scene, {
            dir: [0.8, -0.6, -0.8],
            color: [1.0, 1.0, 1.0],
            intensity: 0.6,
            space: "view",
        });

        new DirLight(viewer.scene, {
            dir: [-0.8, -0.4, -0.4],
            color: [1.0, 1.0, 1.0],
            intensity: 0.6,
            space: "view",
        });

        new DirLight(viewer.scene, {
            dir: [0.2, -0.8, 0.8],
            color: [0.7, 0.7, 0.7],
            intensity: 0.6,
            space: "view",
        });

        return viewer;
    };

    const applyMaterialDefaults = (viewer: Viewer) => {
        if (!configuration) return;

        const { highlightMaterial, selectedMaterial, xrayMaterial } = viewer.scene;
        const { highlight, selected, xray } = configuration.materials;

        Object.assign(highlightMaterial, highlight);
        Object.assign(selectedMaterial, selected);
        Object.assign(xrayMaterial, xray);
    };

    const createViewer = (id: string) => {
        localeService.current = createLocaleService();

        const viewer = new Viewer({
            canvasId: id,
            preserveDrawingBuffer: true,
            spinnerElementId: "spinner",
            dtxEnabled: true,
            transparent: true,
            localeService: localeService.current,
        });

        if (configuration.saoEnabled) {
            const sao = viewer.scene.sao;

            sao.enabled = true;
            sao.intensity = 0.15;
            sao.bias = 0.5;
            sao.scale = 1.0;
            sao.minResolution = 0.0;
            sao.numSamples = 10;
            sao.kernelRadius = 100;
            sao.blendCutoff = 0.1;
        }

        new FastNavPlugin(viewer, {
            hideEdges: true,
            hideSAO: true,
            hidePBR: true,
            hideTransparentObjects: false,
            scaleCanvasResolution: true,
            scaleCanvasResolutionFactor: 0.5,
            delayBeforeRestore: true,
            delayBeforeRestoreSeconds: 0.5,
        });

        setupLights(viewer);
        setupInputEvents(viewer);
        applyMaterialDefaults(viewer);

        // In case we want Y up
        // const { camera } = viewer;
        // camera.worldAxis = [1, 0, 0, 0, 0, 1, 0, -1, 0];

        return viewer;
    };

    const clearViewerCaches = () => {
        cacheRef.current.clear();
        loadOrderRef.current = [];
        lastEntityRef.current = null;
    };

    useEffect(() => {
        const viewer = createViewer(configuration.id);
        if (!viewer) return;

        dispatch(setViewer(viewer));

        const loader = new XKTLoaderPlugin(viewer, {
            id: "xkt-loader",
            objectDefaults: configuration.defaults,
        });

        loader.globalizeObjectIds = true;
        dispatch(setLoaderPlugin(loader));

        if (window.chrome?.webview?.postMessage) {
            window.chrome.webview.postMessage("IfcViewerReady");
        }

        return () => {
            if (flyToScheduledRef.current !== null) {
                cancelAnimationFrame(flyToScheduledRef.current);
            }

            clearViewerCaches();

            viewer.destroy();
            dispatch(setViewer(null));
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!localeService.current) return;
        localeService.current.locale = i18n.language;
    }, [i18n.language]);

    useEffect(() => {
        if (!state.viewer || !state.loaderPlugin) return;

        const cdn = getConfig().cdn;
        const cacheMap = cacheRef.current;
        const currentLoadOrder = loadOrderRef.current;
        const modelSrcs = new Set(state.models.map((m) => m?.modelPath ?? `${m?.url ?? cdn}/${m.folder}/model.xkt`));
        const newLoadOrder: CacheItem[] = currentLoadOrder.filter((item) => modelSrcs.has(item.src));

        let loadedModel = false;

        for (const src of state.models) {
            const modelPath = src?.modelPath ?? `${src?.url ?? cdn}/${src.folder}/model.xkt`;

            if (cacheMap.has(modelPath)) {
                continue;
            }

            const sceneModel = state.loaderPlugin.load({
                id: src.modelId,
                src: modelPath,
                edges: true,
                saoEnabled: true,
            });

            const newModel: CacheItem = { src: modelPath, model: sceneModel };
            cacheMap.set(modelPath, newModel);
            newLoadOrder.push(newModel);
            loadedModel = true;

            sceneModel.on("loaded", () => {
                handleDefaultVisibility();

                if (window.chrome?.webview?.postMessage) {
                    window.chrome.webview.postMessage(`ModelLoaded:=${JSON.stringify(src)}`);
                }

                revealFlyToModel(newModel);
            });
        }

        for (const [src, cacheItem] of cacheMap.entries()) {
            const shouldBeVisible = modelSrcs.has(src);

            if (cacheItem.model.visible !== shouldBeVisible) {
                cacheItem.model.visible = shouldBeVisible;

                if (shouldBeVisible) {
                    handleDefaultVisibility();
                    revealFlyToModel(cacheItem);
                    newLoadOrder.push(cacheItem);
                    loadedModel = true;
                }
            }
        }

        loadOrderRef.current = newLoadOrder;

        if (!loadedModel && newLoadOrder.length > 0) {
            scheduleFlyToModel(newLoadOrder[newLoadOrder.length - 1]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.models]);

    useEffect(() => {
        if (!state.viewer) return;

        const gridObjectKeys = Object.keys(state.viewer.metaScene.metaObjectsByType["IfcGrid"] || {});
        if (viewerState.gridEnabled) {
            dispatchViewer(removeInvisibleEntities(gridObjectKeys));
        } else {
            dispatchViewer(addInvisibleEntities(gridObjectKeys));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerState.gridEnabled]);

    useEffect(() => {
        if (!state.viewer) return;

        const spaceObjectKeys = Object.keys(state.viewer.metaScene.metaObjectsByType["IfcSpace"] || {});
        if (viewerState.spaceEnabled) {
            dispatchViewer(removeInvisibleEntities(spaceObjectKeys));
        } else {
            dispatchViewer(addInvisibleEntities(spaceObjectKeys));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerState.spaceEnabled]);

    return (
        <>
            <canvas id={configuration.id} style={{ height: "100%", width: "100%" }} />
            <div id="spinner" className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="flex w-full h-full justify-center items-center animate-spin text-[#37647b] dark:text-[#f1743c]">
                    <LoaderCircle className="w-16 h-16 animate-spin-slow" />
                </div>
            </div>
            <div
                id="camera-pivot-marker"
                className="absolute pointer-events-none w-3 h-3 border-2 border-black dark:border-white bg-[#37647b] dark:bg-[#f1743c] rounded-full"
            />
        </>
    );
};

export default IfcViewer;
